import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { RoleType, User } from 'api/models';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Select } from 'components/atoms/select';
import { useTranslation } from 'hooks/use-translation';
import { useRolesQuery } from 'queries/roles';

interface Props {
  user: User;
  onSubmit: (roleId: number) => Promise<void>;
  onClose: () => void;
}
const noRoleId = -1;
const Wrapper = styled.div`
  margin-top: var(--spacing-05);
`;

export const ManageRoleModal: FC<Props> = ({ user, onSubmit, onClose }) => {
  const { data } = useRolesQuery();
  const noRoleText = useTranslation('project.members.no_role.label');
  const options = useMemo(
    () =>
      [
        {
          label: noRoleText,
          value: noRoleId,
        },
      ].concat(
        (data ?? [])
          .filter(({ type }) => type === RoleType.Customer)
          .map(({ roleName, roleId }) => ({
            label: roleName,
            value: roleId,
          })),
      ),
    [data, noRoleText],
  );
  const [selectedRole, setSelectedRole] = useState(
    options.find(({ value }) => value === (user.role?.roleId ?? noRoleId)),
  );

  useEffect(() => {
    setSelectedRole(
      options.find(({ value }) => value === (user.role?.roleId ?? noRoleId)),
    );
  }, [options, user.role?.roleId]);

  const [submitInProgress, setSubmitInProgress] = useState(false);
  const onRoleChange = useCallback((selected) => {
    setSelectedRole(selected);
  }, []);
  const onOk = useCallback(() => {
    setSubmitInProgress(true);
    onSubmit(selectedRole?.value ?? noRoleId)
      .then(() => onClose())
      .finally(() => {
        setSubmitInProgress(false);
      });
  }, [onClose, onSubmit, selectedRole?.value]);

  return (
    <Modal
      title={
        user.role?.roleId == null ? (
          <FormattedMessage id="project.members.add_role.label" />
        ) : (
          <FormattedMessage id="project.members.update_role.label" />
        )
      }
      okButtonProps={{
        loading: submitInProgress,
      }}
      cancelButtonProps={{
        type: 'text',
      }}
      okText={<FormattedMessage id="update" />}
      onOk={onOk}
      onCancel={onClose}
      visible
    >
      <Wrapper>
        <Form layout="vertical">
          <Form.Item label={<FormattedMessage id="role" />} required>
            <Select
              options={options}
              value={selectedRole}
              onChange={onRoleChange}
            />
          </Form.Item>
        </Form>
      </Wrapper>
    </Modal>
  );
};
